// Adressen des Leads bearbeiten (Runde 7B, L28): im Abschnitt „Über“ der linken Spalte, ohne Fenster. Je Adresse Art, Straße, Zusatz,
// PLZ, Ort, Bundesland, Land (zwei Buchstaben). „Weitere Adresse“ fügt eine hinzu, der Papierkorb entfernt eine. Speichern ersetzt die
// Liste (`PUT /api/leads/{id}/adressen`, geht über den Schlüssel des Nutzers nach Close). Leere Zeilen fallen weg.
import { useRef, useState } from "react";
import type { Adresse, Lead } from "../../api";
import { leadTiefeApi, type AdresseEingabe } from "../../api/leadTiefe";
import { Icon } from "../Icon";
import { useMeldung } from "../Meldungen";
import { TL } from "./leadTexte";
import "./leadTiefe.css";

const ARTEN = ["business", "mailing", "other"] as const;

function ausAdresse(a: Adresse): AdresseEingabe {
  return {
    label: (a.label ?? "business").toLowerCase(),
    address_1: a.address_1 ?? "",
    address_2: a.address_2 ?? "",
    city: a.city ?? "",
    state: a.state ?? "",
    zipcode: a.zipcode ?? "",
    country: a.country ?? "DE",
  };
}

export function AdresseBearbeiten({ lead, onLead, onFertig }: { lead: Lead; onLead: (l: Lead) => void; onFertig: () => void }) {
  const meldung = useMeldung();
  const [zeilen, setZeilen] = useState<AdresseEingabe[]>(() => {
    const vorhanden = (lead.addresses ?? []).map(ausAdresse);
    return vorhanden.length ? vorhanden : [{ label: "business", country: "DE" }];
  });
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const rahmen = useRef<HTMLDivElement>(null);

  const setzen = (i: number, f: keyof AdresseEingabe, wert: string) =>
    setZeilen((alt) => alt.map((z, j) => (j === i ? { ...z, [f]: wert } : z)));

  const speichern = async () => {
    const land = zeilen.find((z) => (z.country ?? "").trim() && !/^[A-Za-z]{2}$/.test((z.country ?? "").trim()));
    if (land) {
      setFehler(TL.adresse.landHinweis);
      return;
    }
    setFehler(null);
    setLaeuft(true);
    const vorher = lead;
    try {
      const r = await leadTiefeApi.adressenSetzen(lead.id, zeilen);
      onLead((r as { lead: Lead }).lead);
      meldung.ok(TL.adresse.gespeichert);
      onFertig();
    } catch (e) {
      onLead(vorher);
      meldung.fehler(e, TL.adresse.nichtGespeichert);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div
      ref={rahmen}
      className="ad"
      role="group"
      aria-label={TL.adresse.bearbeiten}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          onFertig();
        }
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          void speichern();
        }
      }}
    >
      {zeilen.map((z, i) => (
        <div key={i} className="ad__block">
          <div className="ad__kopf">
            <select className="eingabe eingabe--klein ad__art" aria-label={TL.adresse.art} value={ARTEN.includes((z.label ?? "") as (typeof ARTEN)[number]) ? z.label : "other"} onChange={(e) => setzen(i, "label", e.target.value)}>
              {ARTEN.map((a) => (
                <option key={a} value={a}>
                  {TL.adresse.arten[a]}
                </option>
              ))}
            </select>
            {
              <button
                type="button"
                className="symbolknopf symbolknopf--klein"
                title={TL.adresse.entfernen}
                aria-label={TL.adresse.entfernen}
                onClick={() => setZeilen((alt) => (alt.length > 1 ? alt.filter((_, j) => j !== i) : [{ label: "business", country: "DE" }]))}
              >
                <Icon name="papierkorb" groesse={13} />
              </button>
            }
          </div>
          <input className="eingabe eingabe--klein" placeholder={TL.adresse.strasse} aria-label={TL.adresse.strasse} value={z.address_1 ?? ""} autoFocus={i === 0} onChange={(e) => setzen(i, "address_1", e.target.value)} />
          <input className="eingabe eingabe--klein" placeholder={TL.adresse.zusatz} aria-label={TL.adresse.zusatz} value={z.address_2 ?? ""} onChange={(e) => setzen(i, "address_2", e.target.value)} />
          <div className="ad__zeile">
            <input className="eingabe eingabe--klein ad__plz" placeholder={TL.adresse.plz} aria-label={TL.adresse.plz} value={z.zipcode ?? ""} onChange={(e) => setzen(i, "zipcode", e.target.value)} />
            <input className="eingabe eingabe--klein" placeholder={TL.adresse.ort} aria-label={TL.adresse.ort} value={z.city ?? ""} onChange={(e) => setzen(i, "city", e.target.value)} />
          </div>
          <div className="ad__zeile">
            <input className="eingabe eingabe--klein" placeholder={TL.adresse.bundesland} aria-label={TL.adresse.bundesland} value={z.state ?? ""} onChange={(e) => setzen(i, "state", e.target.value)} />
            <input className="eingabe eingabe--klein ad__land" placeholder="DE" aria-label={TL.adresse.land} title={TL.adresse.landHinweis} maxLength={2} value={z.country ?? ""} onChange={(e) => setzen(i, "country", e.target.value.toUpperCase())} />
          </div>
        </div>
      ))}
      {zeilen.length < 10 ? (
        <button type="button" className="textknopf ad__weitere" onClick={() => setZeilen((alt) => [...alt, { label: "mailing", country: "DE" }])}>
          + {TL.adresse.weitere}
        </button>
      ) : null}
      {fehler ? <div className="ad__fehler">{fehler}</div> : null}
      <div className="ad__fuss">
        <button type="button" className="knopf knopf--primaer knopf--klein" disabled={laeuft} onClick={() => void speichern()}>
          {laeuft ? "Speichert …" : TL.adresse.speichern}
        </button>
        <button type="button" className="knopf knopf--leise knopf--klein" disabled={laeuft} onClick={onFertig}>
          {TL.adresse.abbrechen}
        </button>
      </div>
    </div>
  );
}
